// src/utils/api.js
// Minimal, no imports, no circular refs.
// Smart default: same host the page used, port 5000. REACT_APP_API_BASE_URL can override.

const { protocol, hostname } = window.location;

const defaultBase =
  hostname === 'localhost' || hostname === '127.0.0.1'
    ? `${protocol}//127.0.0.1:5000`
    : `${protocol}//${hostname}:5000`;

// Use env override if present, else default; strip trailing slashes.
const envBase =
  (process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_BASE_URL || '')
    .replace(/\/+$/, '');
export const API_BASE = envBase || defaultBase;

if (typeof window !== 'undefined') {
  window.__API_BASE__ = API_BASE;
}

// -------------------- Added helpers (used by Communications page) --------------------

/**
 * Internal: add credentials and X-User-ID header (matches the rest of your app behavior).
 * Merges any caller-provided headers.
 */
function currentPage() {
  const path = window.location.pathname;
  const map = {
    '/dashboard':   'dashboard',
    '/library':     'library',
    '/editor-home': 'editor_home',
    '/settings':    'settings',
    '/chat':        'chat',
    '/quote':       'quotes',
  };
  if (map[path]) return map[path];
  if (path.startsWith('/reader/') || path.startsWith('/read/') || path.startsWith('/review/')) return 'reader';
  if (path.includes('/designer')) return 'cover_designer';
  if (path.startsWith('/editor/') && path.endsWith('/cover')) return 'book_cover';
  if (path.startsWith('/editor/') && path.endsWith('/blurb')) return 'blurb_editor';
  if (path.startsWith('/editor/')) return 'editor';
  if (path.startsWith('/bookcover/')) return 'cover_designer';
  return 'home';
}

function withAuth(init = {}) {
  const token =
    localStorage.getItem('rv_access_token') ||
    localStorage.getItem('access_token') ||
    '';

  // If body is FormData, do NOT set Content-Type (browser will set multipart boundary)
  const isFormData = init && init.body instanceof FormData;

  return {
    credentials: 'omit',
    ...init,
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json', Accept: 'application/json' }),
      ...(init.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'X-RV-Page': currentPage(),
    },
  };
}

/**
 * Try a silent Cognito refresh, then retry the original request once.
 * Returns the new Response if refresh+retry succeeded, or null.
 *
 * Exported because readerApi.js's PUT and DELETE helpers need exactly this and
 * had been going without it. A stale access token therefore failed every write
 * made from that file on the first 401, while reads and POSTs recovered here —
 * which is why creating a story-element relationship worked and deleting the
 * same relationship did not. One implementation, imported, rather than a second
 * copy that can drift out of step with the token names above.
 */
export async function tryRefreshAndRetry(url, init) {
  const refreshToken = localStorage.getItem('rv_refresh_token');
  if (!refreshToken) return null;

  const cogHosted = window.__COGNITO_HOSTED_UI__ || process.env.REACT_APP_COGNITO_HOSTED_UI || '';
  const cogClient = window.__COGNITO_CLIENT_ID__ || process.env.REACT_APP_COGNITO_CLIENT_ID || '';
  if (!cogHosted || !cogClient) return null;

  try {
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: cogClient,
      refresh_token: refreshToken,
    });
    const resp = await fetch(`${cogHosted}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    if (!resp.ok) return null;
    const tokens = await resp.json();
    if (tokens.access_token) {
      localStorage.setItem('access_token', tokens.access_token);
      localStorage.setItem('rv_access_token', tokens.access_token);
    }
    if (tokens.id_token) {
      localStorage.setItem('id_token', tokens.id_token);
      localStorage.setItem('rv_id_token', tokens.id_token);
    }
    // Retry the request with the fresh token
    const retryInit = { ...init, headers: { ...init.headers, Authorization: `Bearer ${tokens.access_token}` } };
    return fetch(url, retryInit);
  } catch {
    return null;
  }
}

/** Clear all auth data and signal React to redirect */
export function clearSessionAndSignal() {
  localStorage.removeItem('rv_access_token');
  localStorage.removeItem('rv_id_token');
  localStorage.removeItem('rv_refresh_token');
  localStorage.removeItem('access_token');
  localStorage.removeItem('id_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  localStorage.removeItem('user_id');
  window.dispatchEvent(new CustomEvent('rv:unauthorized'));
}

/** GET JSON helper */
export async function getJSON(path) {
  const url = `${API_BASE}${path}`;
  const init = withAuth();
  let res = await fetch(url, init);

  if (res.status === 401) {
    // Token rejected — try a silent refresh + retry before giving up
    const retryRes = await tryRefreshAndRetry(url, init);
    if (retryRes && retryRes.ok) {
      return retryRes.json();
    }
    clearSessionAndSignal();
    return null;
  }
  // 403 = permission denied, NOT an auth failure — don't nuke the session
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `GET ${path} failed: ${res.status}`);
  }
  return res.json();
}


/** POST JSON helper */
export async function postJSON(path, body) {
  const url = `${API_BASE}${path}`;
  const init = withAuth({ method: 'POST', body: JSON.stringify(body || {}) });
  let res = await fetch(url, init);

  if (res.status === 401) {
    const retryRes = await tryRefreshAndRetry(url, init);
    if (retryRes && retryRes.ok) {
      return retryRes.json();
    }
    clearSessionAndSignal();
    return null;
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `POST ${path} failed: ${res.status}`);
  }
  return res.json();
}

/** PATCH JSON helper */
export async function patchJSON(path, body) {
  const url = `${API_BASE}${path}`;
  const init = withAuth({ method: 'PATCH', body: JSON.stringify(body || {}) });
  let res = await fetch(url, init);

  if (res.status === 401) {
    const retryRes = await tryRefreshAndRetry(url, init);
    if (retryRes && retryRes.ok) {
      return retryRes.json();
    }
    clearSessionAndSignal();
    return null;
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `PATCH ${path} failed: ${res.status}`);
  }
  return res.json();
}

/** PUT JSON helper */
export async function putJSON(path, body) {
  const url = `${API_BASE}${path}`;
  const init = withAuth({ method: 'PUT', body: JSON.stringify(body || {}) });
  let res = await fetch(url, init);

  if (res.status === 401) {
    const retryRes = await tryRefreshAndRetry(url, init);
    if (retryRes && retryRes.ok) {
      return retryRes.json();
    }
    clearSessionAndSignal();
    return null;
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `PUT ${path} failed: ${res.status}`);
  }
  return res.json();
}

/* ---------- Comments helpers (used by CommentList) ---------- */
// `type` must match the parent comment's item_type. It used to be hardcoded to
// "book", which meant replies to a comment on a DRAFT queried the wrong item
// and came back empty (and posting one was rejected as a cross-item parent).
export function fetchReplies(bookId, parentId, type = 'book') {
  return getJSON(`/api/books/${bookId}/comments?type=${encodeURIComponent(type)}&parent=${parentId}`);
}

export function postReply(bookId, payload, type = 'book') {
  // payload: { text, parentId, ...optional quote/highlight/page }
  return postJSON(`/api/books/${bookId}/comments?type=${encodeURIComponent(type)}`, payload);
}

export function toggleLike(bookId, commentId, action = 'toggle') {
  // action: 'like' | 'unlike' | 'toggle'
  return postJSON(`/api/books/${bookId}/comments/${commentId}/like`, { action });
}
